import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MessageCircle } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import DisclaimerModal from "@/components/site/DisclaimerModal";
import AREAS from "@/data/practiceAreas";
import useSeo from "@/hooks/useSeo";

const SITE_URL = "https://sikdaradvocates.com";
const WHATSAPP = "918884543456";

export default function PracticeArea() {
  const { slug } = useParams();
  const area = AREAS.find((a) => a.slug === slug);
  useSeo({
    title: area?.seoTitle,
    description: area?.seoDescription,
    canonical: `${SITE_URL}/practice/${slug}`,
  });

  if (!area) return <Navigate to="/#practice" replace />;

  const Icon = area.icon;
  const others = AREAS.filter((a) => a.slug !== slug).slice(0, 4);
  const waMsg = `Hello Sikdar & Sikdar, I have a matter relating to ${area.title}. Could we schedule a consultation?`;

  return (
    <div data-testid={`practice-area-${slug}`} style={{ backgroundColor: "#F9F6F0" }}>
      <DisclaimerModal />
      <Header />

      <main>
        {/* Header band */}
        <section
          data-testid="practice-area-hero"
          className="pt-16 pb-14 md:pt-24 md:pb-20"
          style={{ backgroundColor: "#0A1128", color: "#F9F6F0" }}
        >
          <div className="container-x">
            <Link
              to="/#practice"
              className="inline-flex items-center gap-2 text-[11px] uppercase mb-8"
              style={{ letterSpacing: "0.24em", color: "rgba(249,246,240,0.75)" }}
              data-testid="back-to-practice"
            >
              <ArrowLeft size={14} strokeWidth={1.6} /> All Practice Areas
            </Link>

            <div className="flex items-center gap-4 mb-6" style={{ color: "#C5A059" }}>
              <span
                className="inline-flex items-center justify-center"
                style={{ width: 44, height: 44, border: "1px solid #C5A059" }}
              >
                <Icon size={22} strokeWidth={1.4} />
              </span>
              <span className="text-xs uppercase" style={{ letterSpacing: "0.28em" }}>
                Practice Area
              </span>
            </div>

            <h1
              className="font-serif-display text-5xl md:text-6xl leading-[1.05]"
              style={{ fontWeight: 500, letterSpacing: "-0.005em" }}
            >
              {area.title}
            </h1>

            <p
              className="mt-6 max-w-2xl text-base md:text-lg"
              style={{ color: "rgba(249,246,240,0.82)", lineHeight: 1.7 }}
            >
              {area.blurb}
            </p>
          </div>
        </section>

        {/* Body */}
        <section className="section paper-grain">
          <div className="container-x">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
              <article className="md:col-span-8 space-y-6 text-base md:text-lg" style={{ color: "#334155", lineHeight: 1.8 }}>
                {area.detail.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}

                <div className="mt-10 pt-8" style={{ borderTop: "1px solid #E2DCD0" }}>
                  <h2
                    className="font-serif-display text-2xl md:text-3xl"
                    style={{ color: "#0A1128", fontWeight: 500 }}
                  >
                    Typical matters we handle
                  </h2>
                  <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {area.matters.map((m) => (
                      <li
                        key={m}
                        className="flex items-center gap-3 text-sm"
                        style={{ color: "#0A1128" }}
                      >
                        <span className="inline-block w-4 h-px" style={{ backgroundColor: "#C5A059" }} />
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-10 pt-8 flex flex-wrap items-center gap-3" style={{ borderTop: "1px solid #E2DCD0" }}>
                  <Link to="/#contact" className="btn-primary-sm" data-testid="area-cta-consult">
                    Request a Consultation
                  </Link>
                  <a
                    href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(waMsg)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary-sm"
                    style={{ backgroundColor: "#128C7E", borderColor: "#128C7E", color: "#F9F6F0" }}
                    data-testid="area-cta-whatsapp"
                  >
                    <MessageCircle size={13} strokeWidth={2} /> WhatsApp Us
                  </a>
                </div>
              </article>

              <aside className="md:col-span-4">
                <div
                  className="p-6"
                  style={{ backgroundColor: "#FFFFFF", border: "1px solid #E2DCD0" }}
                >
                  <div className="text-[10px] uppercase" style={{ color: "#7A2021", letterSpacing: "0.24em" }}>
                    Other Practice Areas
                  </div>
                  <ul className="mt-5 space-y-3">
                    {others.map((o) => {
                      const OIcon = o.icon;
                      return (
                        <li key={o.slug}>
                          <Link
                            to={`/practice/${o.slug}`}
                            className="flex items-center justify-between gap-3 py-2"
                            style={{ color: "#0A1128", borderBottom: "1px solid #E2DCD0" }}
                            data-testid={`related-${o.slug}`}
                          >
                            <span className="flex items-center gap-3 text-sm">
                              <OIcon size={16} strokeWidth={1.5} style={{ color: "#7A2021" }} />
                              {o.title}
                            </span>
                            <ArrowUpRight size={14} strokeWidth={1.5} style={{ color: "#C5A059" }} />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
